export type ConnectionPage<TEdge> = {
  edges: TEdge[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
};

/**
 * Reads a cursor-paginated GraphQL connection to its end. `fetchPage` is called
 * with the previous page's `endCursor` until `hasNextPage` is false. Resolves
 * to undefined as soon as a page has no connection, so callers can tell a
 * missing parent apart from an empty list.
 */
export const fetchAllEdges = async <TEdge>(
  fetchPage: (after?: string) => Promise<ConnectionPage<TEdge> | undefined>,
): Promise<TEdge[] | undefined> => {
  const edges: TEdge[] = [];
  let after: string | undefined;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- each page's cursor comes from the previous response
    const page = await fetchPage(after);
    if (!page) {
      return undefined;
    }
    edges.push(...page.edges);

    const { hasNextPage, endCursor } = page.pageInfo;
    if (!hasNextPage || !endCursor) {
      return edges;
    }
    after = endCursor;
  }
};
